import React from 'react';

import {Table} from 'notebook/tables/TableConstants';
import createRequestFulfillmentsTable from 'requests/createRequestFulfillmentsTable';
import {HydratedFulfillmentWithPreparedSampleGroup} from 'requests/fulfillments/HydratedFulfillmentConstants';
import {RequestEntryTableConnectorProps} from 'requests/RequestEntryTable';
import RequestFulfillmentsRetriever from 'requests/RequestFulfillmentsRetriever';
import {restStorableSummariesByIdToGraphQL} from 'samples/storables/graphql/translateStorable';
import APIUtil from 'util/APIUtil';
import InputTableRetriever from 'workflows/stage-entries/components/InputTableRetriever';

import {getAllSamplesByColumn, SamplesSource} from './SamplesSourceCollector';

/**
 * This component is used by the SamplesSourceCollector to retrieve samples that
 * exist in "input" tables. These are tables that are not contained within the
 * note, like normal registration tables or other structured tables. Input tables
 * are rendered BEFORE the note, and are not currently represented internally as
 * Table objects.
 *
 * Note that the category of "input" tables is not limited to input samples. It may
 * include informational tables, if those are needed. This is a category of _position_
 * more than _function_.
 *
 * Currently, this collector will retrieve request fulfillments and workflow inputs, and
 * expose them for use as samples in the AddSamplesFromStructuredTable flow.
 */

interface InputTablesSamplesSourceCollectorProps {
  entryId: string;
  children: (inputSources: Array<SamplesSource>) => JSX.Element;
}

export default function InputTablesSamplesSourceCollector(
  props: InputTablesSamplesSourceCollectorProps
): JSX.Element {
  const sampleCollectionOptions = {
    skipLinkedSamples: false,
    removeDuplicates: false,
    includeUnfinalizedRows: true,
  };
  if (APIUtil.isStageEntry(props.entryId)) {
    return (
      <InputTableRetriever stageEntryId={props.entryId}>
        {(table: Table | null) => {
          if (table == null) {
            return props.children([]);
          }
          const samplesByColumn = getAllSamplesByColumn(table, undefined, undefined, sampleCollectionOptions);
          return props.children(
            samplesByColumn.length > 0
              ? [
                  {
                    name: table.name,
                    icon: 'input',
                    samplesByColumn,
                    table: null,
                  },
                ]
              : []
          );
        }}
      </InputTableRetriever>
    );
  } else {
    return (
      <RequestFulfillmentsRetriever entryId={props.entryId} locked={false} viewer={false}>
        {(params: RequestEntryTableConnectorProps) => {
          if (
            params.fulfillmentIds.length === 0 ||
            params.areContainerLocationsLoading ||
            params.areAnyFulfillmentsInaccessible
          ) {
            return props.children([]);
          }

          const table = createRequestFulfillmentsTable({
            hydratedFulfillments:
              params.hydratedFulfillments as Array<HydratedFulfillmentWithPreparedSampleGroup>,
            containersById: params.containersById,
            ancestralStorablesById: restStorableSummariesByIdToGraphQL(params.ancestralStorablesById!),
            locked: false,
            // Input samples must be finalized to be considered for UAS inclusion
            isFinalized: true,
          });
          table.name = 'Inputs';
          const samplesByColumn = getAllSamplesByColumn(table, undefined, undefined, sampleCollectionOptions);
          return props.children(
            samplesByColumn.length > 0
              ? [
                  {
                    name: table.name,
                    icon: 'input',
                    samplesByColumn,
                    table: null,
                  },
                ]
              : []
          );
        }}
      </RequestFulfillmentsRetriever>
    );
  }
}
